// frontend_app/src/lib/highlight.js
//
// Builds one regex that finds the search terms and (optionally) specimen
// container keywords, and classifies each match for rendering.

import { escapeRegExp, highlightTerms } from './text';

export const SAMPLE_RULES = [
  { name: 'EDTA', aliases: ['edta whole blood', 'edta'], className: 'sample--edta' },
  { name: 'SST', aliases: ['serum separating tube', 'serum separator tube', 'sst'], className: 'sample--sst' },
  { name: 'Lithium heparin', aliases: ['lithium heparin', 'lith hep', 'lih'], className: 'sample--lithium-heparin' },
  { name: 'Urine', aliases: ['24 hour urine', '24hr urine', 'midstream urine', 'random urine', 'urine', 'msu'], className: 'sample--urine' },
  { name: 'Fluoride oxalate', aliases: ['fluoride oxalate', 'fl oxalate', 'grey top', 'gray top', 'glucose tube', 'fluoride'], className: 'sample--fluoride-oxalate' },
  { name: 'Citrate', aliases: ['sodium citrate', 'citrate', 'blue top', 'coag tube', 'coagulation'], className: 'sample--citrate' },
];

const SAMPLE_CLASS_BY_ALIAS = SAMPLE_RULES.reduce((acc, rule) => {
  rule.aliases.forEach((alias) => { acc[alias] = rule.className; });
  return acc;
}, {});

const SAMPLE_PATTERN = Object.keys(SAMPLE_CLASS_BY_ALIAS)
  .sort((a, b) => b.length - a.length)
  .map((alias) => `\\b${escapeRegExp(alias)}\\b`)
  .join('|');

const cache = new Map();

/**
 * Returns `{ regex, classify }` or null when there is nothing to highlight.
 * `classify(text)` gives `{ type: 'term' }`, `{ type: 'sample', className }` or null.
 */
export const buildHighlighter = (query, withSamples) => {
  const terms = highlightTerms(query);
  const key = `${withSamples ? 1 : 0}|${terms.join('\u0000')}`;
  if (cache.has(key)) return cache.get(key);

  const parts = terms.map(escapeRegExp);
  if (withSamples) parts.push(SAMPLE_PATTERN);
  const lowerTerms = new Set(terms.map((t) => t.toLowerCase()));

  const highlighter = parts.length
    ? {
      regex: new RegExp(`(${parts.join('|')})`, 'gi'),
      classify: (text) => {
        const lower = text.toLowerCase();
        if (lowerTerms.has(lower)) return { type: 'term' };
        if (withSamples && SAMPLE_CLASS_BY_ALIAS[lower]) {
          return { type: 'sample', className: SAMPLE_CLASS_BY_ALIAS[lower] };
        }
        return null;
      },
    }
    : null;

  if (cache.size > 50) cache.clear();
  cache.set(key, highlighter);
  return highlighter;
};

/** Split `text` into `[{ text, match }]` segments using a highlighter. */
export const splitForHighlight = (text, highlighter) => {
  if (!text || !highlighter) return [{ text: text || '', match: null }];
  return String(text)
    .split(highlighter.regex)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, match: highlighter.classify(part) }));
};
